import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  buildDbTagPath,
  invalidateTagAcquisitionQueries,
  isSelectOnlyQuery,
  slugifyDatapoint,
  useRegisterDbDatapointMutation,
  type DbSyncMode,
  type RegisterDbDatapointArgs,
  type RegisterDbDatapointConnection
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import {
  buildCadenceOptions,
  DEFAULT_CADENCE_MS,
  formatCadence
} from '../SettingsPage/connection-health/cadence'

/**
 * "Save as data point" — the moment a value returned by an ad-hoc DB query
 * becomes a first-class, scheduled entity. Opened from the DB workbench results
 * pane after a successful read; it captures the value column (+ optional time
 * column for incremental sync), a display name, and a poll cadence, then
 * registers the query as a `db://` data point through the A4 plumbing
 * (`useRegisterDbDatapointMutation` → BFF → registry + graph `:Tag` identity).
 *
 * The dialog stays presentational: it never talks to the proxy directly. The
 * connection posture is supplied by the workbench (webdev gateway vs. direct
 * Postgres), so the registered data point is read back under the exact same
 * connection the query ran against.
 */

/** Radix Select forbids an empty-string value, so the "whole result set" option
 *  (store the entire row set as a non-numeric DataSet) uses this sentinel. */
export const VALUE_WHOLE_SET = '__whole_set__'

const CADENCE_OPTIONS = buildCadenceOptions(DEFAULT_CADENCE_MS)

export interface SaveDatapointForm {
  name: string
  valueColumn: string
  syncMode: DbSyncMode
  timeColumn: string
  cadenceMs: number
  enabled: boolean
}

function initialForm(columns: string[]): SaveDatapointForm {
  return {
    name: '',
    // Default to the first column so a single-scalar KPI query is one click.
    valueColumn: columns[0] ?? VALUE_WHOLE_SET,
    syncMode: 'sample',
    timeColumn: '',
    cadenceMs: DEFAULT_CADENCE_MS,
    enabled: true
  }
}

export type BuildRegistrationResult =
  | { ok: true; args: RegisterDbDatapointArgs; path: string }
  | { ok: false; error: string }

/**
 * Pure builder: turn the dialog form + workbench connection/query into the
 * `RegisterDbDatapointArgs` the A4 mutation expects, or an error string when the
 * form is not registerable. Kept exported and framework-free so the payload
 * shape and gating (SELECT-only, incremental needs a value + time column,
 * whole-set omits the value column, name trimming) are unit-tested directly
 * rather than by driving Radix selects in jsdom.
 */
export function buildDatapointRegistration(input: {
  form: SaveDatapointForm
  connection: RegisterDbDatapointConnection
  query: string
}): BuildRegistrationResult {
  const { form, connection, query } = input
  const name = form.name.trim()
  if (name.length === 0) return { ok: false, error: 'Name is required.' }
  if (query.trim().length === 0) return { ok: false, error: 'Query is empty.' }
  if (!isSelectOnlyQuery(query)) {
    return { ok: false, error: 'Only read-only SELECT queries can be saved as a data point.' }
  }

  const isIncremental = form.syncMode === 'incremental'
  const usesWholeSet = form.valueColumn === VALUE_WHOLE_SET
  const timeColumn = form.timeColumn.trim()

  if (isIncremental && usesWholeSet) {
    return {
      ok: false,
      error: 'Incremental sync needs a specific value column, not the whole result set.'
    }
  }
  if (isIncremental && timeColumn.length === 0) {
    return { ok: false, error: 'Incremental sync needs a time column to watermark on.' }
  }

  const args: RegisterDbDatapointArgs = {
    connection,
    name,
    query,
    ...(usesWholeSet ? {} : { valueColumn: form.valueColumn }),
    ...(isIncremental && timeColumn ? { timeColumn } : {}),
    cadenceMs: form.cadenceMs,
    syncMode: form.syncMode,
    enabled: form.enabled
  }
  return { ok: true, args, path: buildDbTagPath(connection.name, slugifyDatapoint(name)) }
}

export function SaveAsDatapointDialog({
  open,
  onClose,
  connection,
  query,
  columns
}: {
  open: boolean
  onClose: () => void
  connection: RegisterDbDatapointConnection
  query: string
  columns: string[]
}): React.ReactElement {
  const [form, setForm] = React.useState<SaveDatapointForm>(() => initialForm(columns))
  const [error, setError] = React.useState<string | null>(null)
  const [register, { isLoading }] = useRegisterDbDatapointMutation()

  // Re-seed the form each time the dialog opens so a fresh query/result set
  // starts clean (columns can change between runs).
  React.useEffect(() => {
    if (open) {
      setForm(initialForm(columns))
      setError(null)
    }
  }, [open, columns])

  const trimmedName = form.name.trim()
  const isIncremental = form.syncMode === 'incremental'
  const built = buildDatapointRegistration({ form, connection, query })

  // Live preview of the canonical path the server will mint from the name.
  const pathPreview = trimmedName
    ? buildDbTagPath(connection.name, slugifyDatapoint(trimmedName))
    : ''

  // Surface the builder's gating reason as a hint — but not the bare
  // "Name is required" until the user has typed something, so the form doesn't
  // open pre-flagged.
  const validationHint = !built.ok && trimmedName.length > 0 ? built.error : null
  const canSubmit = built.ok && !isLoading

  const handleClose = (): void => {
    if (isLoading) return
    onClose()
  }

  const handleSubmit = async (): Promise<void> => {
    if (!built.ok) return
    setError(null)
    try {
      const result = await register(built.args)

      if (result.failed > 0) {
        setError(result.errors[0]?.reason || 'Registration failed.')
        return
      }

      if (result.alreadyExisted > 0 && result.registered === 0) {
        toast.success(`Data point already registered: ${built.path}`)
      } else {
        toast.success(`Saved data point ${built.path}`)
      }
      invalidateTagAcquisitionQueries()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose()
      }}
    >
      <DialogContent className="sm:max-w-140">
        <DialogHeader>
          <DialogTitle>Save as data point</DialogTitle>
          <DialogDescription>
            Register this query as a scheduled <span className="font-mono">db://</span> data point
            on <span className="font-mono text-ink-2">{connection.name}</span>. It will be polled on
            cadence and become referenceable like any other tag.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 pt-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="save-dp-name">
              Name <span className="text-st-down">*</span>
            </Label>
            <Input
              id="save-dp-name"
              aria-label="Data point name"
              value={form.name}
              placeholder="Line 1 scrap rate"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={isLoading}
            />
            {pathPreview && <span className="font-mono text-11 text-ink-4">{pathPreview}</span>}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="save-dp-value-col">Value column</Label>
            <Select
              value={form.valueColumn}
              onValueChange={(value) => setForm({ ...form, valueColumn: value })}
              disabled={isLoading}
            >
              <SelectTrigger id="save-dp-value-col" aria-label="Value column" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={VALUE_WHOLE_SET}>Whole result set (non-numeric)</SelectItem>
                {columns.map((col) => (
                  <SelectItem key={col} value={col}>
                    {col}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-11 text-ink-4">
              The column whose scalar becomes the reading. Pick a numeric column for a KPI that
              gates on thresholds; the whole result set stores as a non-numeric DataSet.
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="save-dp-sync">Sync mode</Label>
            <Select
              value={form.syncMode}
              onValueChange={(value) => setForm({ ...form, syncMode: value as DbSyncMode })}
              disabled={isLoading}
            >
              <SelectTrigger id="save-dp-sync" aria-label="Sync mode" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sample">
                  Sample — run on cadence, stamp &quot;now&quot;
                </SelectItem>
                <SelectItem value="incremental">
                  Incremental — watermark on a time column
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isIncremental && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="save-dp-time-col">
                Time column <span className="text-st-down">*</span>
              </Label>
              <Select
                value={form.timeColumn}
                onValueChange={(value) => setForm({ ...form, timeColumn: value })}
                disabled={isLoading}
              >
                <SelectTrigger id="save-dp-time-col" aria-label="Time column" className="w-full">
                  <SelectValue placeholder="Select a column…" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((col) => (
                    <SelectItem key={col} value={col}>
                      {col}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-11 text-ink-4">
                Rows newer than the last watermark are backfilled, carrying their own timestamps.
              </span>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="save-dp-cadence">Cadence</Label>
            <Select
              value={String(form.cadenceMs)}
              onValueChange={(value) => setForm({ ...form, cadenceMs: Number(value) })}
              disabled={isLoading}
            >
              <SelectTrigger id="save-dp-cadence" aria-label="Cadence" className="w-full">
                <SelectValue>{formatCadence(form.cadenceMs)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {CADENCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-ink-2">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              disabled={isLoading}
              className="size-3.5 cursor-pointer accent-brand"
            />
            Start polling immediately
          </label>

          {validationHint && !error && (
            <div className="rounded-md bg-st-warn-bg p-2 text-sm text-st-warn">
              {validationHint}
            </div>
          )}
          {error && (
            <div className="rounded-md bg-st-down-bg p-2 text-sm text-st-down">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={isLoading}
            className="text-ink-2 hover:text-ink"
          >
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {isLoading && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
            {isLoading ? 'Saving…' : 'Save data point'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
